import { ID } from "./util";

export type GridSize =
	| "auto"
	| 1
	| 2
	| 3
	| 4
	| 5
	| 6
	| 7
	| 8
	| 9
	| 10
	| 11
	| 12;
/**
 * Remove properties `K` from `T`.
 */
export type Omit<T, K extends keyof any> = T extends any
	? Pick<T, Exclude<keyof T, K>>
	: never;

export type PaletteColorKey =
	| "primary"
	| "secondary"
	| "accent1"
	| "accent2"
	| "accent3"
	| "accent4"
	| "error";

export type TextAlignment = "inherit" | "left" | "center" | "right" | "justify";

export type FieldWidth =
	| "1"
	| "2"
	| "3"
	| "4"
	| "5"
	| "6"
	| "7"
	| "8"
	| "9"
	| "10"
	| "11"
	| "12"
	| "auto";

const fieldWidthToGridSizeMap: Readonly<Record<FieldWidth, GridSize>> = {
	auto: "auto",
	"1": 1,
	"2": 2,
	"3": 3,
	"4": 4,
	"5": 5,
	"6": 6,
	"7": 7,
	"8": 8,
	"9": 9,
	"10": 10,
	"11": 11,
	"12": 12
};
export function mapFieldWidthToGridSize(
	width: FieldWidth
): boolean | GridSize | undefined {
	return fieldWidthToGridSizeMap[width];
}

export interface OptionSource {
	selected: string;
	name: string;
	filter?: string;
	labelKey?: string;
	valueKey?: string;
	url?: string;
	service?: FieldService;
	pendingFillOptionValues?: [string, string];
	additionalRequestData?: object;
}

export interface FieldService {
	service: string;
	namespace?: string;
}
export interface FieldSave {
	type: "button" | "select" | "auto";
}

export interface FieldClick extends FieldService {}

export type GridJustification =
	| "flex-start"
	| "center"
	| "flex-end"
	| "space-between"
	| "space-around"
	| "space-evenly";

export enum ErrorDisplay {
	Never = "Never",
	Touched = "Touched",
	Always = "Always",
	AlwaysNoMessage = "AlwaysNoMessage",
	TouchedNoMessage = "TouchedNoMessage"
}

export enum TemplateDataSource {
	CalcRules = "CalcRules",
	Resource = "Resource"
}

export enum CustomGuiFieldHandling {
	Require = "Require",
	Auto = "Auto",
	Ignore = "Ignore"
}

export interface TemplateTableColumn {
	formTemplateTableColumnId: string;
	name: string;
	tableType: string;
	label?: string;

	showSum?: boolean;
	editable?: boolean;
	isMultipleSelect?: boolean;
	optionValues?: { key: string; value: string }[];
	allOptionValues?: { key: string; value: string }[];
	optionSource?: TableColumnOptionSource;
	isGroupingColumn?: boolean;
	width?: string;
	hrefLink?: string;
	groupLabel?: string;
	footerGroupLabel?: string;
	multiselect?: boolean;
	hideDeleteButton?: boolean;
}

export interface TableColumnOptionSource {
	optionPairCategoryName?: string;
	namespace?: string;
	fieldName?: string;
	key?: string;
	value?: string;
}
export interface TableType {
	displayName: string;
	tableTypeName: string;
	dataSources: DataSource[];
}
export interface DataSource {
	displayName: string;
	sourceName: string;
	isResource: boolean;
	entityId: string;
	namespace: string;
}

export interface TemplateField {
	formTemplateFieldId: string;
	namespace?: string;
	name: string;
	storageName?: string;
	initialFieldValue?: string;

	/** @deprecated Use dedicated properties */
	modifier?: string;

	label?: string;

	/** @deprecated Use helperText */
	tooltip?: string;

	placeholder?: string;
	prefix?: string;

	/** @deprecated Use suffix */
	valueSuffix?: string;

	textAreaRows?: number;
	modalText?: string;

	disabled?: boolean;
	literal?: boolean;
	showHelperTextIfLiteral?: boolean;
	textAlign?: TextAlignment;
	alignWithPrevious?: boolean;
	alwaysVisible?: boolean;
	alwaysEnabled?: boolean;
	isRequired?: boolean;
	hiddenLabel?: boolean;
	width?: FieldWidth;
	role?: string;
	useFieldSeperator?: boolean;

	renderAs?: string;
	variant?: string;

	renderAsLiteral?: string;
	literalVariant?: string;

	horizontal?: boolean;
	color?: string;

	errorDisplay?: ErrorDisplay;
	errorDisplayLiteral?: ErrorDisplay;

	/** @deprecated Use literalErrorDisplay = ErrorDisplay.Never */
	hideErrorWhenLiteral?: boolean;

	hideForMobile?: boolean;
	disableValidation?: boolean;
	fullWidth?: boolean;
	mailto?: boolean;
	alignCenterInParent?: boolean;

	boldText?: boolean;
	italicText?: boolean;

	// TODO: Update DTO and interface-designer
	helperText?: string; // TODO: rename from 'tooltip'?
	suffix?: string; // TODO: rename from 'valueSuffix'?
	margin?: string;
	padding?: string;
	underline?: boolean;
	fontSize?: string;
	disableClearable?: boolean;
	multiple?: boolean;
	manual?: boolean;
	justifyContent?: GridJustification;
	icon?: string;
	uppercaseLabel?: boolean;
	allowEmpty?: boolean;
	allowNegative?: boolean;
	maximumFractionDigits?: number;
	pageToOpen?: string;
	redirectPathKey?: string;
	hiddenIfNoChoice?: boolean;
	searchable?: boolean;
	emptyEqualsZero?: boolean;
	href?: string;
	target?: string;
	to?: string;
	commentType?: string;
	optionPairCategoryName?: string;
	optionValues?: { key: string; value: string }[];
	buttonSize?: "small" | "medium" | "large";
	muiStartIcon?: string;
	muiEndIcon?: string;

	optionSource?: OptionSource;
	search?: FieldService;
	save?: FieldSave;
	hasClick?: boolean;
	click?: FieldClick;
	open?: FieldService;
	globalEvent?: string;

	formatString?: string;

	canResetWriteLocked?: boolean;
	additionalChecks?: string[];

	action?: string;

	cgfHandling?: CustomGuiFieldHandling;
}

export interface TemplateSection {
	formTemplateSectionId: string;
	sectionTemplateId?: number;
	sectionTitle?: string;
	internalDescription?: string;
	style?: string;
	layout?: string;
	fields: TemplateField[];
	literal?: boolean;
	tabGroup?: string;
	tableColumns?: TemplateTableColumn[];

	// Mostly relevant with smart groups. If true, only fields part will matter
	mergeSectionWithPrevious?: boolean;

	/** @deprecated use {@link group} instead. If set it _will_ be converted to group. */
	expansionGroup?: string;

	// Expansion group settings
	group?: string;
	expansionTitle?: string;
	displaySectionTitle?: boolean;
	showWhenCollapsed?: boolean;
	// Shared expansion group settings
	enableExpansion?: boolean;
	expandInitially?: boolean;

	// Shared group settings
	groupIcon?: string;
	outerWidthSM?: GridSize;
	groupBackgroundColor?: string;
	groupBorder?: boolean;
	boldExpansionTitle?: boolean;
	expansionTitleUppercase?: boolean;

	innerWidthSM?: GridSize;
	backgroundColor?: string;
	useSeparator?: boolean;
	elevation?: number;
	marginBottom?: number;
	square?: boolean;
	outline?: PaletteColorKey;
	hideForMobile?: boolean;
	nextLabel?: string;
	prevLabel?: string;

	//	Editable layout options:
	mainText?: string;
	showStatus?: boolean;
	showUpdateSupplier?: boolean;
	hideSectionIfNoVisibleFields?: boolean;
	minimized?: boolean;
	standardMargin?: boolean;
	centerText?: boolean;
	helperText?: string;
	showDialogButtons?: boolean;
	hideSignerList?: boolean;
	hideSignerEditing?: boolean;
	showAlternativeMessage?: boolean;
	fullWidthOpenButton?: boolean;
	showUpdateCoSigner?: boolean;
	useResourceBinding?: boolean;
	tableSchema?: string;
	tableType?: string;
	tableIsEditable?: boolean;
	mergeColumnValues?: boolean;
	submitOnRowChange?: boolean;
	borderlessTable?: boolean;
	tableFilter?: string[];
	multiselect?: boolean;
	canDeleteRow?: boolean;
	canAddNewRows?: boolean;
	masterCheckmarkChecked?: boolean;
	dataSource?: DataSource;
	decisionTableType?: string;
	allowRuntimeColumnAdditions?: boolean;
	emptyTableMessage?: string;
	fullWidth?: boolean;
	allowEditSameSession?: boolean;
	editButtonAtBottom?: boolean;
	hideEditAndSaveIcons?: boolean;
	moveFirstItemToTitle?: boolean;
}

export interface TemplatePanel {
	formPanelId: string;
	panelTitle?: string;
	sections: TemplateSection[];
	initialTab?: string;
	elevation?: number;
	square?: boolean;
	hideForMobile?: boolean;
	desktopViewForMobile?: boolean;
	disableValidation?: boolean;
	literal?: boolean;
	maxWidth?: number;
	minHeight?: number;
}

export interface TemplatePageBase {
	formPageId: string;
	pageTitle?: string;
	pageUri?: string;
	layout?: string;
	prevLabel?: string;
	nextLabel?: string;
	submitLabel?: string;
}

export interface TemplatePage extends TemplatePageBase {
	panels: Record<string, TemplatePanel>;
	description?: string;
	displayInStepper?: boolean;
	headerIconName?: string;
	hideFunctionButtons?: boolean;
	reduceFormHeightForLaptops?: boolean;
	maxWidth?: number;
	backgroundColor?: string;
	showFormStatus?: boolean;
	navigationButtonsSummary?: boolean;
	minHeight?: number;
}

export interface TemplatePageDeprecated extends TemplatePageBase {
	sections: TemplateSection[];
	summary?: TemplateField[];
	summaryLayout?: string;
}

export interface FormTemplate<P extends TemplatePageBase = TemplatePageBase> {
	formTemplateId?: ID;
	name: string;
	description?: string;
	category?: string;
	pages: P[];
	dataSource?: TemplateDataSource;
	resource?: string;
	changeDate?: Date;
	stepper?: string;
}

export interface SectionTemplate {
	sectionTemplateId?: ID;
	name: string;
	description?: string;
	fields: TemplateField[];
	tableColumns?: TemplateTableColumn[];
	tableDataType?: string;
}

export const isDeprecatedPage = (
	page: TemplatePageBase
): page is TemplatePageDeprecated =>
	(page as TemplatePage).panels == null ||
	Object.keys((page as TemplatePage).panels).length === 0;

export const isTemplatePage = (page: TemplatePageBase): page is TemplatePage =>
	!isDeprecatedPage(page);

export type DployFieldName = Pick<TemplateField, "name" | "namespace">;
